import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { api } from "@/lib/api"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { IconShieldLock } from "@tabler/icons-react"
import { useAppSelector } from "@/store"
import { ExportAccessReviewButton, canExportAccessReview } from "./ExportAccessReviewButton"

interface UserDto {
  id: string
  displayName: string
  userPrincipalName: string
}

interface RoleDto {
  id: number
  name: string
}

interface UserAccessDto {
  assignedRoles: number[]
  assignedDirectPermissions: number[]
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<UserDto[]>([])
  const [roles, setRoles] = useState<RoleDto[]>([])
  const [userAccess, setUserAccess] = useState<Map<string, UserAccessDto>>(new Map())
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const { permissions } = useAppSelector(state => state.permissions)

  useEffect(() => {
    async function load() {
      try {
        const [usersData, rolesData] = await Promise.all([
          api.get<UserDto[]>("/api/users"),
          api.get<RoleDto[]>("/api/admin/roles"),
        ])
        setUsers(usersData)
        setRoles(rolesData)

        // Fetch access for each user
        const accessMap = new Map<string, UserAccessDto>()
        await Promise.all(
          usersData.map(async (u) => {
            try {
              const access = await api.get<UserAccessDto>(`/api/admin/users/${u.id}/access`)
              accessMap.set(u.id, access)
            } catch {
              // User may not have any access assigned
            }
          })
        )
        setUserAccess(accessMap)
      } catch (err) {
        console.error("Failed to load admin data:", err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filteredUsers = users.filter(
    (u) =>
      u.displayName.toLowerCase().includes(search.toLowerCase()) ||
      u.userPrincipalName.toLowerCase().includes(search.toLowerCase())
  )

  function getRoleNames(userId: string): string[] {
    const access = userAccess.get(userId)
    if (!access) return []
    return access.assignedRoles
      .map((rid) => roles.find((r) => r.id === rid)?.name)
      .filter(Boolean) as string[]
  }

  function hasDirectPerms(userId: string): boolean {
    const access = userAccess.get(userId)
    return (access?.assignedDirectPermissions.length ?? 0) > 0
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <IconShieldLock className="size-6" />
          <h1 className="text-2xl font-semibold tracking-tight">User Access Management</h1>
        </div>
        {canExportAccessReview(permissions) && <ExportAccessReviewButton />}
      </div>

      <Input
        placeholder="Search users..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Custom</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Link
                      to={`/admin/users/${user.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {user.displayName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.userPrincipalName}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {getRoleNames(user.id).map((name) => (
                        <Badge key={name} variant="secondary">
                          {name}
                        </Badge>
                      ))}
                      {getRoleNames(user.id).length === 0 && (
                        <span className="text-muted-foreground text-sm">None</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {hasDirectPerms(user.id) && (
                      <Badge variant="outline">Custom permissions</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
